import { useCallback } from 'react';
import { useUnit } from 'effector-react';
import { useTranslations } from 'use-intl';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { getIsAddonWasPurshasedEarly } from '@nx-frontend/shared/api/webnotes/get-is-addon-was-purchased-early';
import { generalEnv } from '@nx-frontend/shared/env';
import { useOpenPaywallPopupNew } from '@nx-frontend/shared/hooks/use-navigate';
import { useOpenAddonPopup } from '@nx-frontend/shared/hooks/use-open-addon-popup';
import { needOwnerToPurchasePortalPopup } from '@nx-frontend/shared/popups/need-owner-to-purchase-portal-popup';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $opCanCreatePortal, $opCanManageBilling } from '@nx-frontend/shared/store/org-role';
import { $session } from '@nx-frontend/shared/store/session';
import { BillingAddonCode, BillingInfoPlan, PaywallPopupReason, PopupType } from '@nx-frontend/shared/types';
import { hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';

/**
 * Hook returns callback to open portal addon popup
 * @returns
 */
export const useBuyPortalAddon = () => {
  const openPaywallPopup = useOpenPaywallPopupNew();
  const openAddonPopup = useOpenAddonPopup();
  const t = useTranslations('features');

  const [app, orgInfo, billingInfo, orgCoupons, currentOrganization, opCanManageBilling, opCanCreatePortal, session] =
    useUnit([
      $app,
      $orgInfo,
      $billingInfo,
      $orgCoupons,
      $currentOrganization,
      $opCanManageBilling,
      $opCanCreatePortal,
      $session,
    ]);
  const [openModal] = useUnit([openModalEvent]);

  const openAddonPupup = useCallback(async () => {
    const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
    const isAddonWasPurshasedEarlyOrMaxPlan = !isMaxSubscriptionPlan
      ? await getIsAddonWasPurshasedEarly({
          gatewayUrl: generalEnv.gatewayUrl,
          subscriptionId: billingInfo.subscriptionId,
          addon: BillingAddonCode.PORTALS,
          sessionId: app.sessionId || session.id || '',
          currentSubscriptionCode: billingInfo.plan,
        })
      : true;
    if (!isAddonWasPurshasedEarlyOrMaxPlan) {
      if (opCanManageBilling) {
        openPaywallPopup({
          payload: {
            payloadType: PopupType.PAYWALL,
            mode: 'upgrade',
            title: t('paywall.title_upgrade_now'),
            reason: PaywallPopupReason.PORTAL,
            orgId: currentOrganization.globalId,
          },
        });
      } else {
        if (opCanCreatePortal && billingInfo.plan !== BillingInfoPlan.TRIAL) {
          openAddonPopup({
            payload: {
              payloadType: PopupType.ADDON,
              code: BillingAddonCode.PORTALS,
            },
          });
        } else {
          openModal(needOwnerToPurchasePortalPopup);
        }
      }
    } else {
      openAddonPopup({
        payload: {
          payloadType: PopupType.ADDON,
          code: BillingAddonCode.PORTALS,
        },
      });
    }
  }, [
    orgInfo,
    billingInfo,
    orgCoupons,
    app.sessionId,
    session.id,
    opCanManageBilling,
    openPaywallPopup,
    currentOrganization.globalId,
    opCanCreatePortal,
    openAddonPopup,
    openModal,
    t,
  ]);

  return openAddonPupup;
};
